import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { createFileRoute } from '@tanstack/react-router';
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { isValidJsonKey, LEAD_FIELD_COLUMNS, LEAD_META_KEYS } from '@holymoly/core';
import { PageTitle } from '@/components/PageTitle';
import { Field } from '@/components/ui/Field';
import { useToast } from '@/components/ui/Toaster';
import { errorText } from '@/lib/errors';
import { formatDateTime } from '@/lib/format';
import { requirePermission } from '@/lib/guards';
import { client, orpc } from '@/lib/orpc';
import { useCan } from '@/lib/session';

export const Route = createFileRoute('/_app/crm/mapping')({
  beforeLoad: ({ context }) => requirePermission(context.me, 'crm.view'),
  component: MappingPage
});

type Settings = Awaited<ReturnType<typeof client.crm.settings>>;
type Row = Settings['fieldMap'][number];
type Metadata = Awaited<ReturnType<typeof client.crm.metadata>>;

const kinds = ['field', 'answer', 'meta', 'constant', 'template'] as const;
type Kind = (typeof kinds)[number];
const types = ['string', 'number', 'boolean', 'picklist', 'date', 'json'] as const;
const placeholders = ['{{form}}', '{{campaign}}', '{{firstName}}', '{{lastName}}', '{{company}}', '{{answer:…}}'].join(' ');

const splitSource = (source: string): [Kind, string] => {
  const i = source.indexOf(':');
  const kind = source.slice(0, i) as Kind;
  return [kinds.includes(kind) ? kind : 'constant', source.slice(i + 1)];
};

/** "Antwort = 3" lines ⇄ valueMap. Lines that don't parse are dropped. */
const valueMapToText = (m: Row['valueMap']) => (m ? Object.entries(m).map(([k, v]) => `${k} = ${v}`).join('\n') : '');
function textToValueMap(text: string): Record<string, number> | undefined {
  const entries = text
    .split('\n')
    .map((l) => l.match(/^(.+?)\s*=\s*(-?\d+)\s*$/))
    .filter((m): m is RegExpMatchArray => m !== null)
    .map((m) => [m[1]!.trim(), Number(m[2])] as const);
  return entries.length ? Object.fromEntries(entries) : undefined;
}

function MappingPage() {
  const { t } = useTranslation();
  const can = useCan();
  const status = useQuery(orpc.crm.status.queryOptions());
  const settings = useQuery({ ...orpc.crm.settings.queryOptions(), enabled: status.data?.configured === true });
  const metadata = useQuery({ ...orpc.crm.metadata.queryOptions(), enabled: status.data?.configured === true });
  const crumbs = [{ label: t('crm.section') }, { label: t('crm.mapping'), active: true }];

  if (status.data && !status.data.configured) {
    return (
      <div className="space-y-6 p-6">
        <PageTitle title={t('crm.mapping')} items={crumbs} />
        <div className="alert alert-info alert-soft text-sm">{t('crm.notConfigured')}</div>
      </div>
    );
  }
  if (!settings.data || !metadata.data) return <div className="skeleton m-6 h-64" />;
  return <MappingForm key={settings.data.updatedAt.toString()} initial={settings.data} meta={metadata.data} readOnly={!can('crm.mapping.manage')} />;
}

function MappingForm({ initial, meta, readOnly }: { initial: Settings; meta: Metadata; readOnly: boolean }) {
  const { t } = useTranslation();
  const toast = useToast();
  const can = useCan();
  const queryClient = useQueryClient();
  const [rows, setRows] = useState<Row[]>(initial.fieldMap);
  const [subject, setSubject] = useState(initial.subjectTemplate);
  const [leadSource, setLeadSource] = useState<number | null>(initial.leadSourceValue);
  const [idAttr, setIdAttr] = useState<string | null>(initial.linkedinIdAttribute);
  const [matching, setMatching] = useState(initial.matching);
  const [consents, setConsents] = useState(initial.requiredConsentIds.join(', '));
  const [includeTest, setIncludeTest] = useState(initial.includeTestLeads);

  const attrs = meta.attributes;
  const leadSourceOptions = meta.picklists.filter((p) => p.attribute === 'leadsourcecode');
  const stringAttrs = attrs.filter((a) => ['String', 'Memo'].includes(a.type));

  const consentIds = consents
    .split(/[\s,;]+/)
    .filter(Boolean)
    .map(Number)
    .filter((n) => Number.isInteger(n));
  const draft = {
    fieldMap: rows,
    subjectTemplate: subject.trim() || initial.subjectTemplate,
    leadSourceValue: leadSource,
    linkedinIdAttribute: idAttr,
    matching,
    requiredConsentIds: consentIds,
    includeTestLeads: includeTest
  };
  const invalidJson = rows.some((r) => r.attribute && r.type === 'json' && !(r.jsonKey && isValidJsonKey(r.jsonKey)));
  const dirty =
    JSON.stringify(draft) !==
    JSON.stringify({
      fieldMap: initial.fieldMap,
      subjectTemplate: initial.subjectTemplate,
      leadSourceValue: initial.leadSourceValue,
      linkedinIdAttribute: initial.linkedinIdAttribute,
      matching: initial.matching,
      requiredConsentIds: initial.requiredConsentIds,
      includeTestLeads: initial.includeTestLeads
    });

  const save = useMutation({
    mutationFn: () => client.crm.updateSettings({ ...draft, fieldMap: rows.filter((r) => r.attribute) }),
    onSuccess: async () => {
      toast('success', t('crm.saved'));
      await queryClient.invalidateQueries({ queryKey: orpc.crm.key() });
    },
    onError: (e) => toast('error', errorText(t, e))
  });
  const refresh = useMutation({
    mutationFn: () => client.crm.refreshMetadata({}),
    onSuccess: () => toast('info', t('crm.metadataQueued')),
    onError: (e) => toast('error', errorText(t, e))
  });

  const update = (i: number, patch: Partial<Row>) => setRows((rs) => rs.map((r, j) => (j === i ? { ...r, ...patch } : r)));

  return (
    <div className="space-y-6 p-6">
      <PageTitle
        title={t('crm.mapping')}
        items={[{ label: t('crm.section') }, { label: t('crm.mapping'), active: true }]}
        actions={
          !readOnly && (
            <div className="flex items-center gap-3">
              {dirty && <span className="text-warning text-sm">{t('crm.unsaved')}</span>}
              <button type="button" className="btn btn-primary btn-sm" disabled={!dirty || invalidJson || save.isPending} onClick={() => save.mutate()}>
                {t('crm.save')}
              </button>
            </div>
          )
        }
      />

      <div className="flex flex-wrap items-center gap-3 text-sm">
        <span className="text-base-content/60">{t('crm.metadata')}:</span>
        <span>{meta.syncedAt ? t('crm.metadataSyncedAt', { date: formatDateTime(meta.syncedAt) }) : t('crm.metadataNever')}</span>
        {!readOnly && (
          <button type="button" className="btn btn-ghost btn-xs" disabled={refresh.isPending} onClick={() => refresh.mutate()}>
            <span className="iconify lucide--refresh-cw size-3.5" />
            {t('crm.refreshMetadata')}
          </button>
        )}
      </div>

      <datalist id="crm-attributes">
        {attrs.map((a) => (
          <option key={a.logicalName} value={a.logicalName}>
            {a.label ?? a.logicalName}
          </option>
        ))}
      </datalist>

      <div className="card bg-base-100 shadow-sm">
        <div className="card-body">
          <h2 className="card-title text-base">{t('crm.fieldMap')}</h2>
          <p className="text-base-content/60 text-sm">{t('crm.fieldMapHint')}</p>
          <p className="text-base-content/60 text-sm">{t('crm.jsonHint')}</p>
          <div className="overflow-x-auto">
            <table className="table table-sm">
              <thead>
                <tr>
                  <th>{t('crm.source')}</th>
                  <th>{t('crm.sourceValue')}</th>
                  <th>{t('crm.attribute')}</th>
                  <th>{t('crm.type')}</th>
                  <th />
                </tr>
              </thead>
              <tbody>
                {rows.map((r, i) => (
                  <MappingRowEditor key={i} row={r} readOnly={readOnly} onChange={(p) => update(i, p)} onRemove={() => setRows((rs) => rs.filter((_, j) => j !== i))} />
                ))}
              </tbody>
            </table>
          </div>
          {!readOnly && (
            <div>
              <button
                type="button"
                className="btn btn-ghost btn-sm"
                onClick={() => setRows((rs) => [...rs, { source: 'answer:', attribute: '', type: 'string' }])}
              >
                <span className="iconify lucide--plus size-4" />
                {t('crm.addRow')}
              </button>
            </div>
          )}
        </div>
      </div>

      <div className="grid gap-6 xl:grid-cols-2">
        <div className="card bg-base-100 shadow-sm">
          <div className="card-body">
            <h2 className="card-title text-base">{t('crm.fixed')}</h2>
            <Field label={t('crm.subjectTemplate')} hint={t('crm.subjectHint', { placeholders, interpolation: { escapeValue: false } })}>
              {(id) => <input id={id} className="input w-full" value={subject} maxLength={300} disabled={readOnly} onChange={(e) => setSubject(e.target.value)} />}
            </Field>
            <Field label={t('crm.leadSource')}>
              {(id) => (
                <select id={id} className="select w-full" value={leadSource ?? ''} disabled={readOnly} onChange={(e) => setLeadSource(e.target.value === '' ? null : Number(e.target.value))}>
                  <option value="">{t('crm.none')}</option>
                  {leadSourceOptions.map((o) => (
                    <option key={o.value} value={o.value}>
                      {o.label ?? o.value} ({o.value})
                    </option>
                  ))}
                  {leadSource !== null && !leadSourceOptions.some((o) => o.value === leadSource) && <option value={leadSource}>{leadSource}</option>}
                </select>
              )}
            </Field>
            <Field label={t('crm.linkedinIdAttribute')} hint={t('crm.linkedinIdHint')}>
              {(id) => (
                <select id={id} className="select w-full" value={idAttr ?? ''} disabled={readOnly} onChange={(e) => setIdAttr(e.target.value || null)}>
                  <option value="">{t('crm.none')}</option>
                  {stringAttrs.map((a) => (
                    <option key={a.logicalName} value={a.logicalName}>
                      {a.label ? `${a.label} (${a.logicalName})` : a.logicalName}
                    </option>
                  ))}
                  {idAttr && !stringAttrs.some((a) => a.logicalName === idAttr) && <option value={idAttr}>{idAttr}</option>}
                </select>
              )}
            </Field>
            <label className="label mt-2 cursor-pointer gap-2 text-sm">
              <input type="checkbox" className="checkbox checkbox-sm" checked={includeTest} disabled={readOnly} onChange={(e) => setIncludeTest(e.target.checked)} />
              {t('crm.includeTestLeads')}
            </label>
          </div>
        </div>

        <div className="card bg-base-100 shadow-sm">
          <div className="card-body">
            <h2 className="card-title text-base">{t('crm.matching')}</h2>
            <label className="label cursor-pointer gap-2 text-sm">
              <input
                type="checkbox"
                className="checkbox checkbox-sm"
                checked={matching.contactsActiveOnly}
                disabled={readOnly}
                onChange={(e) => setMatching({ ...matching, contactsActiveOnly: e.target.checked })}
              />
              {t('crm.contactsActiveOnly')}
            </label>
            <Field label={t('crm.multipleMatches')}>
              {(id) => (
                <select
                  id={id}
                  className="select w-full"
                  value={matching.multipleMatches}
                  disabled={readOnly}
                  onChange={(e) => setMatching({ ...matching, multipleMatches: e.target.value as typeof matching.multipleMatches })}
                >
                  {(['flag', 'link_most_recent', 'skip_links'] as const).map((k) => (
                    <option key={k} value={k}>
                      {t(`crm.multiple.${k}`)}
                    </option>
                  ))}
                </select>
              )}
            </Field>
            <Field label={t('crm.consents')} hint={t('crm.consentsHint')}>
              {(id) => <input id={id} className="input w-full font-mono" value={consents} disabled={readOnly} onChange={(e) => setConsents(e.target.value)} />}
            </Field>
          </div>
        </div>
      </div>

      <CampaignMapCard meta={meta} readOnly={readOnly} />
      {can('leads.view_pii') && !readOnly && <PreviewCard fieldMap={rows} subjectTemplate={draft.subjectTemplate} />}
    </div>
  );
}

function MappingRowEditor({ row, readOnly, onChange, onRemove }: { row: Row; readOnly: boolean; onChange: (p: Partial<Row>) => void; onRemove: () => void }) {
  const { t } = useTranslation();
  const [kind, ref] = splitSource(row.source);
  const setSource = (k: Kind, r: string) => onChange({ source: `${k}:${r}` });
  const options = kind === 'field' ? LEAD_FIELD_COLUMNS : kind === 'meta' ? LEAD_META_KEYS : null;

  return (
    <tr className="align-top">
      <td>
        <select
          className="select select-sm w-40"
          aria-label={t('crm.source')}
          value={kind}
          disabled={readOnly}
          onChange={(e) => {
            const k = e.target.value as Kind;
            setSource(k, k === 'field' ? LEAD_FIELD_COLUMNS[0] : k === 'meta' ? LEAD_META_KEYS[0] : '');
          }}
        >
          {kinds.map((k) => (
            <option key={k} value={k}>
              {t(`crm.sourceKinds.${k}`)}
            </option>
          ))}
        </select>
      </td>
      <td>
        {options ? (
          <select className="select select-sm w-48" aria-label={t('crm.sourceValue')} value={ref} disabled={readOnly} onChange={(e) => setSource(kind, e.target.value)}>
            {options.map((o) => (
              <option key={o} value={o}>
                {t(`crm.${kind === 'field' ? 'leadFields' : 'metaKeys'}.${o}` as never)}
              </option>
            ))}
          </select>
        ) : (
          <input className="input input-sm w-56" aria-label={t('crm.sourceValue')} value={ref} disabled={readOnly} onChange={(e) => setSource(kind, e.target.value)} />
        )}
        {row.type === 'picklist' && (
          <textarea
            key={valueMapToText(row.valueMap)}
            className="textarea textarea-sm mt-2 w-56 font-mono text-xs"
            aria-label={t('crm.valueMap')}
            placeholder={t('crm.valueMapHint')}
            rows={3}
            defaultValue={valueMapToText(row.valueMap)}
            disabled={readOnly}
            onBlur={(e) => onChange({ valueMap: textToValueMap(e.target.value) })}
          />
        )}
      </td>
      <td>
        <input
          className="input input-sm w-56 font-mono"
          list="crm-attributes"
          aria-label={t('crm.attribute')}
          value={row.attribute}
          disabled={readOnly}
          onChange={(e) => onChange({ attribute: e.target.value.trim() })}
        />
      </td>
      <td>
        <select
          className="select select-sm w-32"
          aria-label={t('crm.type')}
          value={row.type}
          disabled={readOnly}
          onChange={(e) => {
            const type = e.target.value as Row['type'];
            onChange(type === 'json' ? { type, jsonKey: row.jsonKey ?? '' } : { type, jsonKey: undefined });
          }}
        >
          {types.map((ty) => (
            <option key={ty} value={ty}>
              {t(`crm.types.${ty}`)}
            </option>
          ))}
        </select>
        {row.type === 'json' && (
          <input
            className={`input input-sm mt-2 w-32 font-mono ${row.jsonKey && isValidJsonKey(row.jsonKey) ? '' : 'input-error'}`}
            aria-label={t('crm.jsonKey')}
            placeholder={t('crm.jsonKey')}
            value={row.jsonKey ?? ''}
            disabled={readOnly}
            onChange={(e) => onChange({ jsonKey: e.target.value.trim() })}
          />
        )}
      </td>
      <td>
        {!readOnly && (
          <button type="button" className="btn btn-ghost btn-sm btn-square" aria-label={t('crm.removeRow')} onClick={onRemove}>
            <span className="iconify lucide--trash-2 size-4" />
          </button>
        )}
      </td>
    </tr>
  );
}

function CampaignMapCard({ meta, readOnly }: { meta: Metadata; readOnly: boolean }) {
  const can = useCan();
  const map = useQuery(orpc.crm.campaignMap.queryOptions());
  const campaigns = useQuery({ ...orpc.linkedin.campaigns.queryOptions({ input: { limit: 500 } }), enabled: can('linkedin.view') });
  if (!map.data || !campaigns.data || campaigns.data.length === 0) return null;
  return <CampaignMapEditor key={JSON.stringify(map.data)} meta={meta} readOnly={readOnly} saved={map.data} campaigns={campaigns.data} onSaved={() => void map.refetch()} />;
}

function CampaignMapEditor({
  meta,
  readOnly,
  saved,
  campaigns,
  onSaved
}: {
  meta: Metadata;
  readOnly: boolean;
  saved: { linkedinCampaignId: number; crmCampaignId: string }[];
  campaigns: { id: number; name: string; status: string | null }[];
  onSaved: () => void;
}) {
  const { t } = useTranslation();
  const toast = useToast();
  const [links, setLinks] = useState<Record<number, string>>(() => Object.fromEntries(saved.map((l) => [l.linkedinCampaignId, l.crmCampaignId])));
  const save = useMutation({
    mutationFn: () => client.crm.setCampaignMap({ links: Object.entries(links).map(([li, crm]) => ({ linkedinCampaignId: Number(li), crmCampaignId: crm })) }),
    onSuccess: async () => {
      toast('success', t('crm.saved'));
      onSaved();
    },
    onError: (e) => toast('error', errorText(t, e))
  });
  const crmCampaigns = useMemo(() => meta.campaigns.filter((c) => c.active || Object.values(links).includes(c.id)), [meta.campaigns, links]);

  return (
    <div className="card bg-base-100 shadow-sm">
      <div className="card-body">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div>
            <h2 className="card-title text-base">{t('crm.campaigns')}</h2>
            <p className="text-base-content/60 text-sm">{t('crm.campaignsHint')}</p>
          </div>
          {!readOnly && (
            <button type="button" className="btn btn-sm btn-outline border-base-300" disabled={save.isPending} onClick={() => save.mutate()}>
              {t('crm.save')}
            </button>
          )}
        </div>
        <div className="overflow-x-auto">
          <table className="table table-sm">
            <tbody>
              {campaigns.map((c) => (
                <tr key={c.id}>
                  <td>
                    <p className="font-medium">{c.name}</p>
                    <p className="text-base-content/50 text-xs">{c.status ?? ''}</p>
                  </td>
                  <td className="w-80">
                    <select
                      className="select select-sm w-full"
                      aria-label={c.name}
                      value={links[c.id] ?? ''}
                      disabled={readOnly}
                      onChange={(e) =>
                        setLinks((prev) => {
                          const next = { ...prev };
                          if (e.target.value) next[c.id] = e.target.value;
                          else delete next[c.id];
                          return next;
                        })
                      }
                    >
                      <option value="">{t('crm.noCrmCampaign')}</option>
                      {crmCampaigns.map((cc) => (
                        <option key={cc.id} value={cc.id}>
                          {cc.name}
                        </option>
                      ))}
                    </select>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

function PreviewCard({ fieldMap, subjectTemplate }: { fieldMap: Row[]; subjectTemplate: string }) {
  const { t } = useTranslation();
  const toast = useToast();
  const recent = useQuery(orpc.leads.list.queryOptions({ input: { limit: 20 } }));
  const [picked, setLeadId] = useState('');
  const leadId = picked || recent.data?.items[0]?.id || '';
  const preview = useMutation({
    mutationFn: () => client.crm.preview({ leadId, fieldMap: fieldMap.filter((r) => r.attribute), subjectTemplate }),
    onError: (e) => toast('error', errorText(t, e))
  });

  return (
    <div className="card bg-base-100 shadow-sm">
      <div className="card-body">
        <h2 className="card-title text-base">{t('crm.preview')}</h2>
        <p className="text-base-content/60 text-sm">{t('crm.previewHint')}</p>
        <div className="flex flex-wrap items-end gap-3">
          <Field label={t('crm.previewLead')}>
            {(id) => (
              <select id={id} className="select select-sm w-80" value={leadId} onChange={(e) => setLeadId(e.target.value)}>
                {recent.data?.items.map((l) => (
                  <option key={l.id} value={l.id}>
                    {[l.firstName, l.lastName].filter(Boolean).join(' ') || l.id} · {formatDateTime(l.submittedAt)}
                  </option>
                ))}
              </select>
            )}
          </Field>
          <button type="button" className="btn btn-sm btn-primary mb-1" disabled={!leadId || preview.isPending} onClick={() => preview.mutate()}>
            {t('crm.previewRun')}
          </button>
        </div>
        {preview.data && (
          <>
            <pre className="bg-base-200 mt-2 overflow-x-auto rounded p-3 text-xs">{JSON.stringify(preview.data.payload, null, 2)}</pre>
            {preview.data.skipped.length > 0 && (
              <p className="text-base-content/60 text-sm">{t('crm.previewSkipped', { attributes: preview.data.skipped.map((s) => s.attribute).join(', ') })}</p>
            )}
          </>
        )}
      </div>
    </div>
  );
}
